import { z } from "zod";

export const UI_DECISIONS = [
  "draft",
  "accepted",
  "edited",
  "declined",
  "pending",
  "excluded",
  "locked",
] as const;
export type UiDecision = (typeof UI_DECISIONS)[number];

export const ENTRY_STATUSES = [
  "rating_pending",
  "draft",
  "submitted",
  "approved",
  "locked",
  "excluded",
] as const;

export const linkReviewCyclesSchema = z
  .object({
    linkedReviewCycleIds: z.array(z.string().min(1)).min(1).max(24),
    ratingLookbackWindowDays: z.number().int().min(30).max(730).optional(),
    version: z.number().int().min(0),
  })
  .strict();
export type LinkReviewCyclesInput = z.infer<typeof linkReviewCyclesSchema>;

export const eligibleReviewCycleSchema = z.object({
  id: z.string(),
  name: z.string(),
  lifecycle: z.string(),
  currentStage: z.string(),
  reviewPeriodStart: z.string().nullable(),
  reviewPeriodEnd: z.string().nullable(),
  signOffCompletedAt: z.string().nullable(),
  completesBeforeFlag: z.boolean(),
  archetype: z.string(),
  supported: z.boolean(),
  alreadyLinked: z.boolean(),
});
export type EligibleReviewCycle = z.infer<typeof eligibleReviewCycleSchema>;

export const eligibleReviewCyclesSchema = z.object({
  rows: z.array(eligibleReviewCycleSchema),
});

export const reviewPreviewResolvedSchema = z.object({
  membershipId: z.string(),
  name: z.string(),
  sourceCycleId: z.string(),
  sourceCycleName: z.string(),
  ratingValue: z.number(),
  ratingLabel: z.string(),
  signedOffAt: z.string(),
  pinned: z.boolean(),
});

export const reviewPreviewPendingSchema = z.object({
  membershipId: z.string(),
  name: z.string(),
  pending: z.literal(true),
  reason: z.enum(["no_finalized_rating", "pinned_cycle_missing"]),
});

export const reviewCyclePreviewSchema = z.object({
  rows: z.array(z.union([reviewPreviewResolvedSchema, reviewPreviewPendingSchema])),
  summary: z.object({
    resolved: z.number(),
    pending: z.number(),
    windowStart: z.string(),
    windowEnd: z.string(),
  }),
});
export type ReviewCyclePreview = z.infer<typeof reviewCyclePreviewSchema>;

export const patchCompEntrySchema = z
  .object({
    version: z.number().int().min(0),
    acceptSuggested: z.boolean().optional(),
    zeroMeritSelected: z.boolean().optional(),
    promotionFlag: z.boolean().optional(),
    promotionTargetGradeId: z.string().optional(),
    finalMeritPercent: z.string().optional(),
    finalMeritAmount: z.string().optional(),
  })
  .strict();
export type PatchCompEntryInput = z.infer<typeof patchCompEntrySchema>;

export const excludeCompEntrySchema = z
  .object({
    version: z.number().int().min(0),
    reason: z.string().max(200).optional(),
  })
  .strict();
export type ExcludeCompEntryInput = z.infer<typeof excludeCompEntrySchema>;

export const compPlanPotSchema = z.object({
  amount: z.string(),
  used: z.string(),
  remaining: z.string(),
  overPot: z.boolean(),
});
export type CompPlanPot = z.infer<typeof compPlanPotSchema>;

export const compPlanEntrySchema = z.object({
  worksheetId: z.string().optional(),
  id: z.string(),
  membershipId: z.string(),
  name: z.string(),
  department: z.string(),
  status: z.string(),
  uiDecision: z.enum(UI_DECISIONS),
  ratingValue: z.number().nullable(),
  ratingLabel: z.string().nullable(),
  currentBase: z.string(),
  currentCtc: z.string(),
  currentVariable: z.string(),
  recMeritPercent: z.string().nullable(),
  recMeritAmount: z.string().nullable(),
  revisedCtc: z.string(),
  finalMeritPercent: z.string().nullable(),
  finalMeritAmount: z.string().nullable(),
  zeroMeritSelected: z.boolean(),
  promotionFlag: z.boolean().default(false),
  promotionTargetGradeId: z.string().nullable().default(null),
  promotionNewBase: z.string().nullable().default(null),
  promotionBandMin: z.string().nullable().default(null),
  promotionBandMax: z.string().nullable().default(null),
  excludedReason: z.string().nullable(),
  plannerEditedAt: z.string().nullable(),
  sourceAssignmentVersion: z.number().nullable(),
  version: z.number(),
  ratingsStale: z.boolean().default(false),
});
export type CompPlanEntry = z.infer<typeof compPlanEntrySchema>;

export const compPlanSpendSchema = z.object({
  pot: z.string(),
  proposedTotal: z.string(),
  averageHikePercent: z.string(),
  overUnder: z.string(),
});

export const compDepartmentSpendSchema = z.object({
  department: z.string(),
  pot: z.string(),
  proposed: z.string(),
  averageHikePercent: z.string(),
  difference: z.string(),
  overUnder: z.enum(["Over", "Under"]),
});

export const compPlanSchema = z.object({
  totalEntries: z.number().optional(),
  truncated: z.boolean().optional(),
  worksheets: z
    .array(
      z.object({
        id: z.string(),
        status: z.string(),
        version: z.number(),
        plannerMembershipId: z.string(),
        allocatedAmount: z.string(),
      }),
    )
    .optional(),
  worksheetId: z.string(),
  worksheetStatus: z.string(),
  worksheetVersion: z.number().optional(),
  cycleId: z.string(),
  postingStatus: z.string(),
  pot: compPlanPotSchema,
  spend: compPlanSpendSchema.optional(),
  departments: z.array(compDepartmentSpendSchema).default([]),
  entries: z.array(compPlanEntrySchema),
});
export type CompPlan = z.infer<typeof compPlanSchema>;

export const worksheetSummarySchema = z.object({
  id: z.string(),
  cycleId: z.string(),
  status: z.string(),
  sendBackReason: z.string().nullable(),
  submittedAt: z.string().nullable(),
  entryCount: z.number(),
  version: z.number(),
});
export type WorksheetSummary = z.infer<typeof worksheetSummarySchema>;

export const mineWorksheetEntrySchema = z.object({
  id: z.string(),
  membershipId: z.string(),
  name: z.string(),
  recMeritPercent: z.string().nullable(),
  recMeritAmount: z.string().nullable(),
  recNewBase: z.string().nullable(),
  status: z.string(),
});

export const mineWorksheetSchema = z.object({
  id: z.string(),
  cycleId: z.string(),
  cycleName: z.string(),
  currency: z.string().optional(),
  entryCount: z.number().optional(),
  entriesTruncated: z.boolean().optional(),
  status: z.string(),
  version: z.number(),
  entries: z.array(mineWorksheetEntrySchema),
});

export const mineWorksheetsSchema = z.object({
  rows: z.array(mineWorksheetSchema),
  limitReached: z.boolean().optional(),
});
export type MineWorksheet = z.infer<typeof mineWorksheetSchema>;
